/**
 * Danny Web App
 *
 * Main application component with authentication and routing.
 */

import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { OnboardingProvider } from "./contexts/OnboardingContext";
import { useOnboarding } from "./contexts/OnboardingContext";
import { ChatSyncProvider } from "./contexts/ChatSyncContext";
import { BackendHealthGate } from "./components/BackendHealthGate";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { OAuthCallbackHandler } from "./components/OAuthCallbackHandler";
import { Dashboard, ChatPopOut } from "./features";
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from "./features/auth";
import { LandingRouter } from "./features/home/LandingRouter";
import { PrivacyPolicyPage } from "./features/home/PrivacyPolicyPage";
import { TermsOfServicePage } from "./features/home/TermsOfServicePage";
import { SharedViewPage } from "./features/shared-view/SharedViewPage";
import { AcceptSharedListInvitePage } from "./pages/AcceptSharedListInvitePage";
import { SharedListsPage } from "./features/shared-lists/SharedListsPage";
import { SharedListDetailPage } from "./features/shared-lists/SharedListDetailPage";
import { OnboardingWizard } from "./pages/OnboardingWizard";
import { useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { TelemetryProvider, usePageTracking, useAppOpened } from "./telemetry";

/**
 * Reads auth context to bootstrap the TelemetryProvider with the Statsig
 * client key and server-side bootstrap data.
 */
function TelemetryProviderWrapper({ children }: { children: React.ReactNode }) {
  const { statsigBootstrap } = useAuth();
  const clientKey = import.meta.env.VITE_STATSIG_CLIENT_KEY as
    | string
    | undefined;

  return (
    <TelemetryProvider clientKey={clientKey ?? ""} bootstrap={statsigBootstrap}>
      {children}
    </TelemetryProvider>
  );
}

/**
 * Fires global tracking hooks (page views, app opened).
 * Renders nothing — purely side-effect.
 */
function TelemetryTracking() {
  usePageTracking();
  useAppOpened();
  return null;
}

/**
 * Settings has no standalone route — it lives inside the Dashboard (mounted at
 * /app) and opens via the `?tab=` search param. But several links still point at
 * /settings and /settings/:tab: the Getting Started checklist CTAs, the
 * upgrade/trial "manage billing" buttons, transactional-email deep links, and
 * the Stripe checkout return URL. Those paths matched no route and rendered a
 * blank page (the reported "/settings/integrations is blank" bug). Redirect the
 * whole family onto /app?tab=…, preserving any other query params (e.g.
 * checkout=success). A path segment (/settings/integrations) wins over an
 * existing ?tab=; a bare /settings defaults to the account tab so it still
 * opens settings rather than dropping the user on the task list.
 */
function SettingsRedirect() {
  const { tab } = useParams();
  const [searchParams] = useSearchParams();
  const params = new URLSearchParams(searchParams);
  params.set("tab", tab ?? params.get("tab") ?? "account");
  return <Navigate to={`/app?${params.toString()}`} replace />;
}

function WizardGuard({ children }: { children: React.ReactNode }) {
  const { wizardComplete, isLoading } = useOnboarding();
  const { search } = useLocation();
  // Skip the wizard funnel when the user arrived from a transactional email
  // (?from=email). Welcome confirmation, digest, comment notifications, etc.
  // all tag their links — once we know the user is engaging via email, the
  // wizard's "show me your stuff" purpose is moot and bouncing them feels
  // like a bug. GettingStartedChecklist on the dashboard re-surfaces any
  // unfinished steps in-place.
  const fromTransactional = new URLSearchParams(search).get('from') === 'email';
  if (isLoading) return null;
  if (!wizardComplete && !fromTransactional) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

/**
 * Main App Export with Routing and Auth
 */
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OnboardingProvider>
          <TelemetryProviderWrapper>
            <TelemetryTracking />
            <Routes>
              {/* Public auth routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />

              {/* OAuth callback routes */}
              <Route
                path="/auth/google/callback"
                element={<OAuthCallbackHandler provider="google" />}
              />
              <Route
                path="/auth/github/callback"
                element={<OAuthCallbackHandler provider="github" />}
              />

              {/* Onboarding wizard — protected, redirects to /app when complete */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <OnboardingWizard />
                  </ProtectedRoute>
                }
              />

              {/* Public pages */}
              <Route path="/" element={<LandingRouter />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/shared/:shareId" element={<SharedViewPage />} />
              <Route
                path="/invite/:token"
                element={<AcceptSharedListInvitePage />}
              />

              {/* Authenticated app — behind ProtectedRoute */}
              <Route
                path="/app"
                element={
                  <ProtectedRoute>
                    <WizardGuard>
                      <ChatSyncProvider>
                        <BackendHealthGate>
                          <Dashboard />
                        </BackendHealthGate>
                      </ChatSyncProvider>
                    </WizardGuard>
                  </ProtectedRoute>
                }
              />
              {/* Task deep-links from transactional email (e.g. /tasks/:id).
                These URLs only appear in the welcome-confirmation and similar
                emails — a user clicking one has already engaged with the
                product. Skip WizardGuard so we don't bounce them to step 2 of
                onboarding mid-flow; the GettingStartedChecklist on the dashboard
                surfaces any remaining steps in-place. Bare /tasks still funnels
                through the wizard since it has no specific deep-link intent. */}
              <Route
                path="/tasks/:taskId"
                element={
                  <ProtectedRoute>
                    <ChatSyncProvider>
                      <BackendHealthGate>
                        <Dashboard />
                      </BackendHealthGate>
                    </ChatSyncProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tasks"
                element={
                  <ProtectedRoute>
                    <WizardGuard>
                      <ChatSyncProvider>
                        <BackendHealthGate>
                          <Dashboard />
                        </BackendHealthGate>
                      </ChatSyncProvider>
                    </WizardGuard>
                  </ProtectedRoute>
                }
              />
              {/* Legacy /settings* links → canonical /app?tab=… (see SettingsRedirect) */}
              <Route path="/settings" element={<SettingsRedirect />} />
              <Route path="/settings/:tab" element={<SettingsRedirect />} />
              <Route
                path="/app/shared-lists"
                element={
                  <ProtectedRoute>
                    <SharedListsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/shared-lists/:listId"
                element={
                  <ProtectedRoute>
                    <SharedListDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chat/popout"
                element={
                  <ProtectedRoute>
                    <ChatSyncProvider isPopOut>
                      <ChatPopOut />
                    </ChatSyncProvider>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </TelemetryProviderWrapper>
        </OnboardingProvider>
        <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
